import React, { Dispatch, SetStateAction } from 'react'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { usePagination } from '../hooks/use-pagination'
import { searchQuery } from '../zod/pagination'
import { useTranslation } from 'react-i18next'
import { useCandidates } from '../candidate/hooks'
import { CandidateTable, CandidateTableProps } from '../candidate/components/candidate-table'
import { INDEX_CANDIDATE } from '../user/permission'
import { useTitle } from '../hooks/use-title'
import { ContainerOutlined, ExpandOutlined, ProjectOutlined, TableOutlined } from '@ant-design/icons'
import { Board } from '../campaign/components/board/board'
import { z } from 'zod'
import { useLocalStorage } from '../hooks/use-local-storage'
import { Candidate } from '../candidate/candidate'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../auth'
import { NotSubscribedResult } from '../components/not-subscribed-result'
import { Button, Flex, Segmented, Tooltip } from 'antd'
import { CandidateModal } from '../candidate/components/candidate-modal'
import { CandidateTimestampToggle } from '../components/candidate-timestamp-toggle'
import { CandidateAttributesToggle } from '../components/candidate-attributes-toggle'
import { CandidatePhoneToggle } from '../components/candidate-phone-toggle'
import { CandidateArchiveButton } from '../candidate/components/candidate-archive-button'
import { CandidateArchiveDrawer } from '../candidate/components/candidate-archive-drawer'
import { PageTitle } from '../components/page-title'

export const Route = createFileRoute('/_auth/_main/candidates/')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(INDEX_CANDIDATE)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    validateSearch: searchQuery.and(z.object({ candidateId: z.string().optional(), archive: z.boolean().optional() })),
    component: Candidates,
})

function Candidates() {
    const { t } = useTranslation()
    const search = Route.useSearch()
    const navigate = useNavigate()
    const [selected, setSelected] = React.useState<Record<string, boolean>>({})
    const [listTotal, setListTotal] = React.useState<number>()

    const [view, setView] = useLocalStorage({
        key: 'candidates-view',
        defaultValue: 'board',
    })

    const [, setCandidatesFullscreen] = useLocalStorage({
        key: 'candidates-fullscreen',
        defaultValue: false,
    })

    useTitle(t('Candidates'))

    const { hasSubscription, isAdmin } = useAuth()

    if (!hasSubscription) {
        return <NotSubscribedResult />
    }

    const openCandidateModal = (candidate: Candidate) => () => {
        navigate({
            to: '.',
            search: {
                ...search,
                candidateId: candidate.id,
            },
        })
    }

    const closeCandidateModal = () => {
        navigate({
            to: '.',
            search: {
                ...search,
                candidateId: undefined,
            },
        })
    }

    const isBoardVisible = view === 'board' && !isAdmin

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: t('Candidates'),
                    },
                ]}
            />
            <PageTitle
                title={t('Candidates')}
                count={isBoardVisible ? undefined : listTotal}
                countLoading={!isBoardVisible && listTotal === undefined}
                extra={
                    <Flex gap="small">
                        {isBoardVisible && (
                            <Flex align="center">
                                <CandidateTimestampToggle />
                                <CandidateAttributesToggle />
                                <CandidatePhoneToggle />
                                <Tooltip title={t('Fullscreen')}>
                                    <Link
                                        to="/board"
                                        onClick={() => setCandidatesFullscreen(true)}
                                    >
                                        <Button
                                            type="text"
                                            icon={<ExpandOutlined />}
                                        />
                                    </Link>
                                </Tooltip>
                                <Tooltip title={t('Archive')}>
                                    <Link
                                        to="."
                                        search={{
                                            archive: true,
                                        }}
                                    >
                                        <Button
                                            type="text"
                                            icon={<ContainerOutlined />}
                                        />
                                    </Link>
                                </Tooltip>
                                {Object.keys(selected).length > 0 && (
                                    <CandidateArchiveButton
                                        onSuccess={() => setSelected({})}
                                        candidateIds={Object.keys(selected)}
                                    />
                                )}
                            </Flex>
                        )}
                        {!isAdmin && (
                            <Segmented
                                value={view}
                                onChange={(value) => {
                                    setView(value as never)
                                }}
                                options={[
                                    {
                                        label: (
                                            <Flex gap="small">
                                                {t('List')} <TableOutlined />
                                            </Flex>
                                        ),
                                        value: 'list',
                                    },
                                    {
                                        label: (
                                            <Flex gap="small">
                                                {t('Board')} <ProjectOutlined />
                                            </Flex>
                                        ),
                                        value: 'board',
                                    },
                                ]}
                            />
                        )}
                    </Flex>
                }
            />
            {isBoardVisible ? (
                <BoardView
                    selected={selected}
                    setSelected={setSelected}
                />
            ) : (
                <ListView
                    onRow={(record) => {
                        return {
                            onClick: openCandidateModal(record),
                        }
                    }}
                    search={search}
                    onTotalChange={setListTotal}
                />
            )}
            <CandidateModal
                candidateId={search.candidateId}
                open={search.candidateId !== undefined}
                onCancel={closeCandidateModal}
            />
            <CandidateArchiveDrawer
                open={search.archive}
                onClose={() => {
                    navigate({
                        to: '.',
                        search: (prev) => ({
                            ...prev,
                            archive: undefined,
                        }),
                    })
                }}
            />
        </>
    )
}

function BoardView({
    selected,
    setSelected,
}: {
    selected: Record<string, boolean>
    setSelected: Dispatch<SetStateAction<Record<string, boolean>>>
}) {
    const { activeCompany } = useAuth()

    if (!activeCompany) {
        return null
    }

    return (
        <Board
            height="60vh"
            columns={activeCompany.columns}
            selected={selected}
            setSelected={setSelected}
        />
    )
}

function ListView({
    search,
    onTotalChange,
    ...rest
}: {
    search: z.infer<typeof searchQuery>
    onTotalChange?: (total: number | undefined) => void
} & Partial<CandidateTableProps>) {
    const tableProps = usePagination(useCandidates, {
        query: {
            ...search,
            include: ['campaign'],
        },
    })

    const total = tableProps.pagination ? tableProps.pagination.total : undefined

    React.useEffect(() => {
        onTotalChange?.(total)
    }, [total, onTotalChange])

    return (
        <CandidateTable
            {...tableProps}
            {...rest}
        />
    )
}
